<template>
  <ControlWrapper v-bind="controlWrapper" :styles :is-focused :applied-options :error="control.errors">
    <SectionWrapper
      :title="computedLabel"
      :description="controlOptions.description || ''"
      :title-class="styles.control.label"
    >
      <template #header="{ title }">
        <div class="flex w-full items-center justify-between" :class="styles.control.label">
          <h3>{{ title }}</h3>
          <div v-if="control?.data?.latitude" class="flex items-center gap-2">
            <WButton type="ghost" @click="openRepairOverlay">
              <WidgetIcon :icon="themeIcons.edit" height="1.5rem" class="widget-action-icon" />
            </WButton>
            <WButton type="ghost" @click="isOpenDeleteModal = true">
              <WidgetIcon :icon="themeIcons.delete" height="1.5rem" class="widget-action-icon-negative" />
            </WButton>
          </div>
        </div>
      </template>
      <div v-if="!controlData?.latitude || !controlData?.longitude" class="flex w-full flex-col items-center gap-8">
        <div class="flex w-full flex-col items-center">
          <WidgetIcon v-if="controlOptions.empty?.icon" :icon="controlOptions.empty?.icon" class="text-primary">
          </WidgetIcon>
          <p v-if="controlOptions.empty?.description" class="max-w-sm text-center">
            {{ controlOptions.empty?.description }}
          </p>
          <WButton :type="checkTheme('common') ? 'primary' : 'outline'" class="mt-8" @click="openRepairOverlay">{{
            controlOptions.empty?.btnText
          }}</WButton>
        </div>
      </div>
      <div v-else class="flex w-full flex-col items-center gap-8">
        <RepairCard :editable="false" :location="controlData"></RepairCard>
      </div>

      <OverlayRepairFinder
        v-model="isRepairOverlayOpen"
        :data="controlData"
        editable
        @save="handleSaveData"
        @close="handleClose"
      />
    </SectionWrapper>
    <Modal v-if="isOpenDeleteModal" v-model="isOpenDeleteModal" hide-close>
      <template #header><p class="title2-bold">Delete repair location?</p> </template>
      <div class="flex h-full w-full flex-col items-start gap-4">
        <p>Are you sure you want to delete this location?</p>
        <p>This action can not be undone</p>
      </div>
      <template #footer>
        <div class="w-full">
          <WButton type="secondary" class="widget-confirm-modal_btn_close" @click="isOpenDeleteModal = false">
            No, keep location
          </WButton>
          <WButton type="primary" class="widget-confirm-modal_btn" @click="deleteLocation"> Yes, I am sure </WButton>
        </div>
      </template>
    </Modal>
  </ControlWrapper>
</template>

<script lang="ts" setup>
  import { computed, ref } from 'vue';
  import { ControlElement } from '@jsonforms/core';
  import { rendererProps, useJsonFormsControl } from '@jsonforms/vue';
  import { useWidgetControl } from '../../util';
  import ControlWrapper from '../ControlWrapper.vue';
  import SectionWrapper from '../../../claims-widget/components/SectionWrapper.vue';
  import WidgetIcon from 'src/components/base/WidgetIcon.vue';
  import { RepairLocation } from 'src/composables/repairFinder';
  import OverlayRepairFinder from './OverlayRepairFinder.vue';
  import WButton from 'src/components/base/Button.vue';
  import RepairCard from './RepairCard.vue';
  import { checkTheme, themeIcons } from 'src/utils/config';
  import Modal from 'src/components/base/ConfirmModal.vue';

  export interface RepairFinderConfig {
    title: string;
    description: string;
    type: string;
    repairFinder: boolean;
    nearCollisionText: string;
    empty: Empty;
  }

  export interface Empty {
    description: string;
    btnText: string;
    icon: string;
  }

  const props = defineProps({
    ...rendererProps<ControlElement>(),
  });
  const isOpenDeleteModal = ref(false);

  const { control, controlWrapper, styles, isFocused, appliedOptions, onChange, computedLabel } = useWidgetControl(
    useJsonFormsControl(props)
  );
  const controlData = computed<RepairLocation | undefined>(() => {
    return control.value?.data;
  });
  const controlOptions = computed(() => control.value?.schema as Partial<RepairFinderConfig>);
  const isRepairOverlayOpen = ref(false);

  function handleSaveData(payload: RepairLocation) {
    onChange(payload);
    handleClose();
  }

  function handleClose() {
    isRepairOverlayOpen.value = false;
  }

  function openRepairOverlay() {
    isRepairOverlayOpen.value = true;
  }

  function deleteLocation() {
    onChange(undefined);
    isOpenDeleteModal.value = false;
  }
</script>
